import React from 'react';
import { motion } from 'framer-motion';

export default function PrivacyPolicy() {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="container py-16 space-y-12"
    >
      <section className="section--tint-light rounded-lg p-12 space-y-6">
        <h1 className="text-4xl font-bold text-center">Privacy Policy</h1>
        <p className="text-center text-gray-600 italic">Last updated: 26 August 2026</p>

        <div className="bg-white rounded-md border border-gray-200 p-6 space-y-3">
          <h2 className="text-2xl font-semibold">What changed in this update</h2>
          <p>
            We have updated this policy to explain uploaded images and videos, moderation checks,
            access controls, and deletion. In plain language:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Uploaded media and related moderation records are now described clearly.</li>
            <li>We explain quarantine storage, automated checks, video frame sampling, optimization, and thumbnails.</li>
            <li>We explain signed media access, access logging, support review, retained evidence, and audit trails.</li>
            <li>We explain when uploaded media files may be deleted while limited metadata or evidence may remain.</li>
            <li>We have clarified OpenAI moderation processing alongside our other service providers.</li>
            <li>We explain structured observer ride reports, revision history, admin review, and recipient acknowledgements.</li>
            <li>We explain digital ride declarations, drawn signatures, short-lived proof codes, and recorded pre-ride checks.</li>
          </ul>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-semibold mb-2">Who We Are</h2>
            <p>
              Member Nest is operated by <strong>Inclusion Project Management Limited</strong>,
              a UK company (Company No. 10879394). If you have any questions about your data or
              this policy, email <strong>privacy@member-nest.com</strong>.
            </p>
            <p className="mt-2">
              For account, platform, security, billing, and support activity, Inclusion Project
              Management Limited is responsible for how Member Nest uses your personal data. Clubs
              using Member Nest are responsible for the membership information, profile fields,
              events, communications, and club rules they choose to manage through the platform.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Information We Collect</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Account details, such as name, email address, username, login details, and account identifiers</li>
              <li>Profile and membership details, such as date of birth, address, emergency contacts, family links, tags, roles, and club-specific profile fields</li>
              <li>Optional private demographic answers, including a separately held date of birth for age bands and selected characteristics such as ethnicity, ancestry, disability, religion, language, gender, or sexual orientation</li>
              <li>Club activity, such as memberships, join requests, event bookings, RSVPs, waitlists, attendance, documents accepted, and subscription choices</li>
              <li>Training and observation records, including ride details, observer reports, structured answers, report revisions, admin review notes, and acknowledgements</li>
              <li>Ride declaration records, including immutable wording versions, IAM membership-number snapshots, drawn signature strokes, evidence hashes, acceptance times, and authorised pre-ride check records</li>
              <li>Payment and billing records, including Stripe customer, subscription, checkout, refund, payout, and payment status references</li>
              <li>Club annual-billing and closure-settlement evidence, including qualification dates, inclusion or exclusion reasons, charge attempts, invoices, card health, and lock audit records</li>
              <li>Optional treasurer reconciliation records, including payment-round assignments, amounts, dates, methods, references, waivers, and private administrator notes</li>
              <li>Communications, including inbox messages, email campaign records, message board posts, replies, event comments, uploaded media, mentions, notifications, and communication preferences</li>
              <li>Media upload information, including file name, type, size, storage paths, thumbnails, optimization details, upload status, access records, and the club, board, topic, message, or conversation where media is used</li>
              <li>Moderation information, including automated flag reasons, sampled video frame results, member reports, review notes, audit trails, restrictions, hidden-content snapshots, and retained review evidence</li>
              <li>Technical and security information, such as IP address, device or browser information, logs, rate-limit records, signed media access records, and authentication events</li>
            </ul>
            <p className="mt-2">
              Please do not put special category data, such as health, medical, biometric, or
              safeguarding information, into free-text posts, messages, comments, or media uploads
              unless the club has clearly requested it in an appropriate place and has explained why
              it is needed.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">How We Collect Information</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Directly from you when you register, join a club, update your profile, pay, post, upload media, report content, or contact support</li>
              <li>From club administrators when they invite members, configure clubs, update membership records, or manage events</li>
              <li>From parents or guardians when they create or manage child or family accounts</li>
              <li>Automatically from the app, hosting providers, storage providers, moderation providers, payment providers, email providers, and security logs</li>
            </ul>
            <p className="mt-2">
              We do not use advertising cookies or third-party ad tracking. Some local device storage
              may be used for app preferences and signed-in product features.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Why We Use Information</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>To provide accounts, club membership tools, events, payments, communications, notifications, and support</li>
              <li>To show information to the right club members, admins, parents, guardians, or support users based on access settings</li>
              <li>With your consent, to reuse private demographic answers and produce disclosure-protected totals for clubs you belong to; clubs cannot access individual answers or completion status</li>
              <li>To let authorised observers create training reports, let recipients review and acknowledge them, and let clubs oversee those records</li>
              <li>To let members sign current ride declarations, present short-lived proof, and let authorised group checkers record that proof was checked before a ride</li>
              <li>To process payments, subscriptions, refunds, payout records, and accounting integrations</li>
              <li>To send service messages, club messages, board notifications, event updates, and emails based on your settings</li>
              <li>To quarantine, scan, optimize, thumbnail, access-control, display, and delete uploaded media where needed to provide and protect the service</li>
              <li>To detect, review, prevent, and respond to spam, abuse, illegal content, safeguarding risks, fraud, and security issues</li>
              <li>To maintain audit trails, resolve disputes, enforce our terms, comply with legal duties, and improve Member Nest</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Private Demographic Reporting</h2>
            <p>
              If you choose to create a demographic profile, Member Nest stores it separately from
              club member records. Your consent allows compatible answers to be reused automatically
              in protected aggregate reports for clubs you currently or later belong to. Club
              administrators cannot view individual answers or see who has responded.
            </p>
            <p className="mt-2">
              Reports require a minimum number of participants, suppress small result groups, and
              limit repeated runs to reduce the risk of identifying a person by comparison. A parent
              or guardian may answer while using a managed child account. You can withdraw globally
              and delete the underlying answers from your profile; previously created anonymous
              aggregate snapshots remain unchanged.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Lawful Bases</h2>
            <p>
              We use personal data where it is needed to provide the service or take steps requested
              before providing it, where we or a club have a legitimate interest in running and
              protecting Member Nest, where we need to comply with legal obligations, and where consent
              is appropriate, such as for some optional communications or preferences.
            </p>
            <p className="mt-2">
              Our legitimate interests include securing the platform, preventing misuse, moderating
              message boards and uploaded media, supporting clubs and members, keeping records of
              important actions, and improving the service in ways users would reasonably expect.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Message Boards, Uploaded Media, and Moderation</h2>
            <p>
              Board posts, replies, and event comments are stored with the author, timestamps, content,
              mentions, edit history, moderation status, and related club or event identifiers. They
              may be visible to other members, admins, parents, guardians, or support users depending
              on the board, event, tag, and club settings.
            </p>
            <p className="mt-2">
              Member Nest may automatically check board content for moderation signals. Automated
              flags can create review cases and may temporarily hide high-risk content. Club admins
              and Member Nest support may review reported or flagged content, add notes, take action,
              and retain limited evidence and audit records where needed for safety, dispute handling,
              legal compliance, or platform protection.
            </p>
            <p className="mt-2">
              Where uploaded media is enabled, files are first stored in quarantine. Images may be
              checked directly and videos may be checked using sampled frames. We may use OpenAI
              moderation services to help identify potentially unsafe media. Approved media may be
              optimized or transcoded, given thumbnails, moved to approved storage, and served through
              access checks and time-limited signed access URLs. We may record when approved media is
              accessed so we can protect access, troubleshoot availability, and investigate misuse.
            </p>
            <p className="mt-2">
              Member Nest support may review uploaded media that is flagged, reported, blocked, or
              held for review. Support users may see moderation signals, upload context, recent
              relevant messages or board content, and temporary previews where needed to decide
              whether media should remain blocked, be approved as a false positive, or be deleted.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Who We Share Information With</h2>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Clubs you join</strong> - administrators can see and export member information needed to run the club</li>
              <li><strong>Other members</strong> - where you post, reply, comment, RSVP, or appear in features visible to them</li>
              <li><strong>Parents or guardians</strong> - where family or child account features apply</li>
              <li><strong>Stripe</strong> - for payments, subscriptions, refunds, payouts, securely held club billing cards, and off-session annual Member Nest charges where club-managed membership is selected</li>
              <li><strong>Firebase and Google Cloud</strong> - for authentication, hosting, databases, cloud functions, storage, logging, and security</li>
              <li><strong>OpenAI</strong> - for automated content moderation checks on text or uploaded media where those features are enabled</li>
              <li><strong>Resend</strong> - for service emails, club emails, and notification emails</li>
              <li><strong>Xero</strong> - where a club chooses to connect accounting features</li>
              <li><strong>Professional, legal, safeguarding, or law enforcement contacts</strong> - where reasonably necessary or legally required</li>
            </ul>
            <p className="mt-2">We do not sell personal data or share it with advertisers.</p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Club Record Migrations</h2>
            <p>
              A club may provide existing membership records to Member Nest when it moves from
              another system. These records may include contact and profile details, family or
              guardian relationships, emergency contacts, membership options, tags, and the date
              through which membership has already been paid. The club is responsible for deciding
              what it needs to migrate and for having an appropriate lawful basis.
            </p>
            <p className="mt-2">
              Imported records do not automatically create personal login accounts. We send a
              time-limited claim invitation, require a verified matching email, and ask the member or
              guardian to review the information before it is linked to an account. Original CSV
              content is not retained after normalized migration records are prepared. Unclaimed or
              cancelled records and claim links can be removed by the club in line with its retention
              responsibilities.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">How Long We Keep Information</h2>
            <p>
              We keep information for as long as needed to provide Member Nest, run clubs, meet legal
              and accounting requirements, resolve disputes, protect users, and maintain security.
              Account deletion removes or disconnects account records where possible, but some records
              may remain where needed, such as payment records, annual-billing closure archives, audit logs, club records, board content
              already shared in a club space, deleted-content evidence retained for moderation review,
              or records a club must keep.
            </p>
            <p className="mt-2">
              Uploaded media files may be deleted when you discard an unused upload, when incomplete
              or unused uploads are cleaned up, when content is removed through moderation, when board
              content is deleted, or when direct-message media is no longer visible to any participant.
              File deletion does not always remove all related records immediately: upload metadata,
              access records, moderation cases, audit trails, and limited evidence may remain where
              needed for safety, dispute handling, legal compliance, or platform protection.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Your Rights</h2>
            <p>
              You can access, update, delete, or manage much of your information directly in Member
              Nest. You may also ask for access, correction, deletion, restriction, objection, data
              portability, or withdrawal of consent where those rights apply. Some requests may need to
              be handled by the relevant club if the data is controlled by that club.
            </p>
            <p className="mt-2">
              You can contact us at <strong>privacy@member-nest.com</strong>. You also have the right
              to complain to the Information Commissioner's Office at <strong>ico.org.uk</strong>.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Children's Data</h2>
            <p>
              Member Nest supports clubs and families that include children. Child accounts and child
              member records may be managed by parents, guardians, or authorised club administrators.
              Child data is not intended to be public, but it may be visible to the relevant club,
              family users, event organisers, and support users where needed to provide and protect the
              service.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">International Transfers</h2>
            <p>
              Some providers, including Google Cloud/Firebase, Stripe, Resend, and Xero, may process
              information outside the UK. Where that happens, we rely on appropriate safeguards or
              transfer mechanisms required by UK data protection law.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Automated Decisions</h2>
            <p>
              We use automated checks to help flag potentially risky board content, uploaded media,
              spam, abuse, or security issues. These checks support moderation and security workflows.
              They are not intended to make final legal or similarly significant decisions without
              human review.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Changes to This Policy</h2>
            <p>
              If we make significant changes, we will take reasonable steps to tell users, such as by
              email or in-app notice.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Contact Us</h2>
            <p>Questions or concerns? Email <strong>privacy@member-nest.com</strong>.</p>
          </div>
        </div>
      </section>
    </motion.main>
  );
}
